/**
 * Detects if the user should receive mobile/tablet layouts.
 * Uses both user agent hints and viewport width so tablet breakpoints apply
 * consistently during live assessments.
 *
 * Fine-pointer laptops (trackpad/mouse) always keep the desktop chrome — including
 * touch-capable Windows machines where maxTouchPoints > 1. Overlay nav is reserved
 * for phones, tablets, and coarse-pointer devices.
 */
import { useState, useEffect } from 'react';

const MOBILE_TABLET_REGEX = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|Tablet|tablet|Kindle|Silk/i;
const TABLET_MAX_WIDTH_PX = 1200;
const MOBILE_MAX_WIDTH_PX = 768; // Phones vs tablets
const TABLET_UA_REGEX = /iPad|Tablet|tablet|Kindle|Silk|PlayBook|Nexus 7|Nexus 9|SM-T|Tab/i;
/** Viewports below this on fine-pointer desktops get a light UI scale (≈13″ laptops). */
export const COMPACT_DESKTOP_MIN_WIDTH_PX = 1024;
export const COMPACT_DESKTOP_MAX_WIDTH_PX = 1439;

export function isFinePointerDesktop(): boolean {
  try {
    if (typeof window === 'undefined') return true;
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  } catch {
    return true;
  }
}

function isTouchFirstDevice(): boolean {
  try {
    if (typeof window === 'undefined') return false;
    return (
      window.matchMedia('(pointer: coarse)').matches ||
      window.matchMedia('(hover: none)').matches
    );
  } catch {
    return false;
  }
}

export function isCompactDesktopViewport(): boolean {
  try {
    if (typeof window === 'undefined') return false;
    if (!isFinePointerDesktop()) return false;
    const width = window.innerWidth;
    return width >= COMPACT_DESKTOP_MIN_WIDTH_PX && width <= COMPACT_DESKTOP_MAX_WIDTH_PX;
  } catch {
    return false;
  }
}

export function isLandscapeViewport(): boolean {
  try {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(orientation: landscape)').matches;
  } catch {
    return false;
  }
}

/** Side-by-side "My progress" + QA panes on tablet landscape (e.g. Surface Pro), not phone portrait. */
export function preferWideDashboardPanes(
  isMobileOrTablet: boolean,
  isTablet: boolean,
  isLandscape: boolean,
  width: number
): boolean {
  if (!isMobileOrTablet) return true;
  if (!isLandscape) return false;
  return isTablet || width >= 900;
}

function detectMobileOrTablet(): boolean {
  try {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    const isUaMobileOrTablet = MOBILE_TABLET_REGEX.test(ua);
    // iPad in desktop mode (iOS 13+): reports as Mac.
    const isIPadDesktopMode = navigator.maxTouchPoints > 1 && /Macintosh/.test(ua);
    const fineDesktop = isFinePointerDesktop();
    const isTabletViewport =
      !fineDesktop && window.matchMedia(`(max-width: ${TABLET_MAX_WIDTH_PX}px)`).matches;
    // Touch-first devices up to common tablet widths — not touch-capable laptops.
    const isTouchTabletLike =
      !fineDesktop &&
      isTouchFirstDevice() &&
      window.matchMedia('(max-width: 1366px)').matches;
    // Larger Windows/Android tablets (e.g. Surface) often exceed 1366px and do not
    // match mobile UAs. Coarse pointer + no hover is a reliable tablet/phone signal
    // and avoids laptop trackpads (hover + fine pointer).
    const isCoarseWithoutHover =
      window.matchMedia('(pointer: coarse)').matches && window.matchMedia('(hover: none)').matches;
    return isUaMobileOrTablet || isIPadDesktopMode || isTabletViewport || isTouchTabletLike || isCoarseWithoutHover;
  } catch {
    return false;
  }
}

function detectDeviceBreakdown(): {
  isMobileOrTablet: boolean;
  isMobile: boolean;
  isTablet: boolean;
  isCompactDesktop: boolean;
  isLandscape: boolean;
} {
  const isMobileOrTablet = detectMobileOrTablet();
  const width = typeof window !== 'undefined' ? window.innerWidth : 1024;
  const height = typeof window !== 'undefined' ? window.innerHeight : 768;
  const ua = typeof navigator !== 'undefined' ? (navigator.userAgent || '') : '';
  const isIPadDesktopMode = typeof navigator !== 'undefined' && navigator.maxTouchPoints > 1 && /Macintosh/.test(ua);
  // Use shortest side so phones stay "mobile" in landscape (width alone can exceed 768px).
  const shortestSide = Math.min(width, height);
  const isAndroidTabletUa = /Android/i.test(ua) && !/Mobile/i.test(ua);
  const isTabletUa = TABLET_UA_REGEX.test(ua) || isIPadDesktopMode || isAndroidTabletUa;
  const isLikelyTabletBySize = shortestSide >= MOBILE_MAX_WIDTH_PX;
  const isTablet = isMobileOrTablet && (isTabletUa || isLikelyTabletBySize);
  const isMobile = isMobileOrTablet && !isTablet;
  const isCompactDesktop = !isMobileOrTablet && isCompactDesktopViewport();
  const isLandscape = isLandscapeViewport();
  return { isMobileOrTablet, isMobile, isTablet, isCompactDesktop, isLandscape };
}

/** Sidebar starts open on desktop; closed when the nav is the mobile/tablet overlay. */
export function getInitialSidebarOpen(): boolean {
  if (typeof window === 'undefined') return true;
  return !detectDeviceBreakdown().isMobileOrTablet;
}

export function useDeviceType(): {
  isMobileOrTablet: boolean;
  isMobile: boolean;
  isTablet: boolean;
  isCompactDesktop: boolean;
  isLandscape: boolean;
} {
  const [state, setState] = useState(() => detectDeviceBreakdown());

  useEffect(() => {
    const update = () => setState(detectDeviceBreakdown());
    update();
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    const hoverMq = window.matchMedia('(hover: hover)');
    const coarseMq = window.matchMedia('(pointer: coarse)');
    const fineMq = window.matchMedia('(pointer: fine)');
    const landscapeMq = window.matchMedia('(orientation: landscape)');
    hoverMq.addEventListener('change', update);
    coarseMq.addEventListener('change', update);
    fineMq.addEventListener('change', update);
    landscapeMq.addEventListener('change', update);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
      hoverMq.removeEventListener('change', update);
      coarseMq.removeEventListener('change', update);
      fineMq.removeEventListener('change', update);
      landscapeMq.removeEventListener('change', update);
    };
  }, []);

  return state;
}
